// collector/photoQueue.js
//
// The collector's OWN photos (collector-app P2-C part C, locked answer L-H): a
// shelf photo is shrunk on the phone, kept as a Blob in the `photos` store of
// collector/db.js with its uuid, SHOWN AT ONCE from that Blob, and uploaded in
// the background — one at a time, and only when every tap is already sent.
// A photo never blocks a mark, and a `facts` tap never waits for its photos: it
// names them by the id the server will give them (collector/photos.js
// `collectorPhotoPath`), so a report made with no signal goes into the queue
// like any tap and the pictures follow.
//
//   add({ blob, lineId })   write the Blob to the phone and show it (the upload
//                           follows once the taps are sent); resolves `{ uuid, id }` — `id` is what the
//                           `facts` tap carries in `photoIds`
//   localPhotos             what the day shows on top of the server's document:
//                           the pictures the server does not have yet, from
//                           their Blobs (collector/dayModel.js `withLocalPhotos`)
//   flush()                 upload what waits
//   holdInvoicePhoto / heldInvoicePhoto / releaseInvoicePhoto
//                           the invoice's paper: kept on the phone until that
//                           shop's invoice is filed. It rides the filing request
//                           (`POST /collector-shop-invoices` has a `photo` field),
//                           so the queue never uploads it. The same row carries
//                           the filing while that waits to be sent
//                           (collector/invoiceQueue.js): a paper taken then
//                           replaces the Blob and keeps what he filed.
//
// WHEN IT UPLOADS: the action queue empty (`pendingCount === 0`, nothing in
// flight) and the login alive; the engine's own triggers — the signal coming
// back, the page becoming visible, `pageshow`, the taps draining — and a 60 s
// tick while photos wait. ONE upload at a time, in the order they were taken,
// under its own cross-tab lock (the Chrome tab and the installed app share one
// storage). A network error, a timeout or a 5xx keeps the photo and backs off
// like the action queue (5 → 10 → 20 → 60 s); the same uuid is sent again, and
// the server answers a repeat with the same id and writes nothing. A 401/419
// pauses everything on the login (L-D). A 403, 404 or 422 that is OUR API's own
// JSON answer is final (the server will never take that photo): the Blob is
// deleted and the collector is told ONCE, by a `photo_refused` card on the day
// page (collector-offline P2-A) — the photos behind it still go. Any other 4xx
// (a proxy's 413, a captive portal's 403: a page, not our API) is the network
// talking, never a verdict: the photo is kept and tried again with the backoff.
// After the server answers the Blob is deleted: the row keeps only its receipt
// until plan close (L-I).
//
// A PINIA STORE for the same reason as the action layer: it must outlive the
// page that took the picture (F7 removes the previous page on every push).
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch } from 'vue';
import { BACKOFF_MS, TICK_MS, isOurApi, useCollectorActionsStore } from '@/collector/actions.js';
import { findLine } from '@/collector/dayModel.js';
import {
  PHOTO_HELD,
  PHOTO_KIND,
  PHOTO_QUEUED,
  PHOTO_UPLOADED,
  collectorStorage,
  filedJustAfter,
  invoiceRowKey,
  sameId,
} from '@/collector/db.js';
import { newActionId } from '@/collector/ids.js';
import { collectorNetwork } from '@/collector/network.js';
import { collectorPhotoPath, sendCollectorPhoto } from '@/collector/photos.js';
import {
  authPaused,
  collectorUserId,
  normalizeUserId,
  pauseForAuth,
  registerLocalWork,
} from '@/collector/session.js';
import { usePwa } from '@/composables/usePwa';
import { shopKey, useCollectorDayStore } from '@/stores/collectorDayStore.js';

const PHOTO_LOCK = 'poolit-collector-photos';
const LOCK_RETRY_MS = 3_000;

/** The card state this queue makes (CollectorConflictCards words it). */
export const PHOTO_CARD = Object.freeze({ REFUSED: 'photo_refused' });

/**
 * Our API's own answers a photo can never get past, named the way the invoice
 * queue names its: the phone's word for the status, not a copy of the backend's.
 */
const REFUSED_STATUS = Object.freeze({ 403: 'not_collector', 404: 'not_found', 422: 'invalid' });

const byTaken = (a, b) => String(a.createdAt).localeCompare(String(b.createdAt));

/** A photo whose upload is still owed (a shelf photo not yet answered). */
const isQueued = (row) => row?.kind !== PHOTO_KIND.INVOICE && row?.status === PHOTO_QUEUED && row?.blob != null;

function makePreview(blob) {
  try {
    return typeof URL?.createObjectURL === 'function' ? URL.createObjectURL(blob) : null;
  } catch {
    return null;
  }
}

function revokePreview(url) {
  try {
    if (url) URL.revokeObjectURL?.(url);
  } catch {
    /* nothing to free */
  }
}

/** The row a photo leaves behind once the server has it: no Blob, just the receipt. */
function receiptOf(row, stored) {
  const { blob: _blob, ...rest } = row;
  return { ...rest, status: PHOTO_UPLOADED, id: stored.id, url: stored.url ?? null, uploadedAt: new Date().toISOString() };
}

export const useCollectorPhotosStore = defineStore('collectorPhotos', () => {
  /** Shelf photos still owed to the server, oldest first — Blobs inside (plain objects, replaced never mutated). */
  const rows = shallowRef([]);
  /** uuid → object URL of the Blob, for showing it before the server has it. */
  const previews = shallowRef({});
  const flushing = ref(false);
  const started = ref(false);
  const nextAttemptAt = ref(0);

  /** Photos waiting for the server — the sync pill's count. */
  const pendingCount = computed(() => rows.value.length);

  /** What the day shows over the server's document: `{ uuid, lineId, planId, preview }`. */
  const localPhotos = computed(() =>
    rows.value
      .filter((row) => previews.value[row.uuid])
      .map((row) => ({ uuid: row.uuid, lineId: row.lineId, planId: row.planId, preview: previews.value[row.uuid] })),
  );

  const storage = () => collectorStorage();

  // ── Memory ─────────────────────────────────────────────────────────────────

  /** The user whose rows are in memory (undefined = nobody loaded yet). */
  let loadedFor;
  let loading = null;
  /** Rows whose write to the phone failed: put again before the next upload. */
  const unsaved = new Map();
  // While a photo lives only in memory, a reload would lose it: hold one (the
  // collector's service-worker update applies itself at idle — usePwa). A phone
  // whose storage stays full keeps the hold, and with it the old worker, until
  // the photo is written or uploaded: the photo comes first.
  let releaseUnsavedHold = null;
  function syncUnsavedHold() {
    if (unsaved.size > 0 && !releaseUnsavedHold) {
      releaseUnsavedHold = usePwa().holdReload('collector-photos-unsaved');
    } else if (unsaved.size === 0 && releaseUnsavedHold) {
      releaseUnsavedHold();
      releaseUnsavedHold = null;
    }
  }
  /** uuids that left memory in this session (uploaded or dropped) — a stale read must not bring them back. */
  const gone = new Set();

  function hold(row) {
    if (rows.value.some((held) => held.uuid === row.uuid)) return;
    rows.value = [...rows.value, row].sort(byTaken);
    if (!previews.value[row.uuid]) {
      const url = makePreview(row.blob);
      if (url) previews.value = { ...previews.value, [row.uuid]: url };
    }
  }

  function forget(uuid) {
    gone.add(uuid);
    unsaved.delete(uuid);
    syncUnsavedHold();
    rows.value = rows.value.filter((row) => row.uuid !== uuid);
    if (previews.value[uuid]) {
      revokePreview(previews.value[uuid]);
      const { [uuid]: _removed, ...rest } = previews.value;
      previews.value = rest;
    }
  }

  function resetMemory() {
    for (const url of Object.values(previews.value)) revokePreview(url);
    rows.value = [];
    previews.value = {};
    unsaved.clear();
    syncUnsavedHold();
    gone.clear();
    clearTimers();
  }

  /** Merge what the phone holds: its queued photos join, ones another tab uploaded leave. */
  function merge(stored = []) {
    for (const row of stored) {
      if (!row?.uuid) continue;
      if (row.status === PHOTO_UPLOADED) {
        if (rows.value.some((held) => held.uuid === row.uuid)) forget(row.uuid);
        continue;
      }
      if (isQueued(row) && !gone.has(row.uuid)) hold(row);
    }
  }

  /** The stored photos in memory before anything is uploaded; memoized per user, never rejects. */
  function ensureLoaded() {
    const userId = collectorUserId();
    if (userId == null && loadedFor != null) return loading ?? Promise.resolve();
    if (loading && loadedFor === userId) return loading;
    if (loadedFor !== undefined && loadedFor !== userId) resetMemory();
    loadedFor = userId;
    const work = storage()
      .loadPhotos(userId)
      .then((stored) => {
        if (loadedFor === userId) merge(stored);
        return true;
      })
      .catch((error) => {
        console.error('[collector/photoQueue] reading the stored photos failed', error);
        loading = null; // the next call reads again
        return false;
      });
    loading = work;
    return work;
  }

  async function reconcile() {
    try {
      merge(await storage().loadPhotos(loadedFor ?? null));
    } catch (error) {
      console.warn('[collector/photoQueue] reconcile with the stored photos failed', error);
    }
  }

  async function writeUnsaved() {
    for (const row of [...unsaved.values()]) {
      try {
        await storage().putPhoto(row);
        unsaved.delete(row.uuid);
        syncUnsavedHold();
      } catch (error) {
        console.warn('[collector/photoQueue] a photo is still not on the phone', error);
      }
    }
  }

  // ── Taking a photo ─────────────────────────────────────────────────────────

  /**
   * Keep a shrunk photo on the phone and show it. Resolves with its uuid and
   * the id a `facts` tap names it by. A failed write does not lose the photo —
   * it stays in memory (logged) and is written again before the next upload.
   *
   * @param {{ blob: Blob, lineId: string|number, planId?: string|number }} input
   * @returns {Promise<{ uuid: string, id: string }>}
   */
  async function add({ blob, lineId, planId = null }) {
    await ensureLoaded();
    const dayStore = useCollectorDayStore();
    const plan = planId ?? dayStore.activePlanId ?? dayStore.serverDay?.id ?? null;
    const uuid = newActionId();
    const id = collectorPhotoPath({ planId: plan, lineId, uuid }); // validates plan and line before anything is kept
    const row = {
      uuid,
      planId: String(plan),
      lineId: String(lineId),
      userId: loadedFor ?? null,
      kind: PHOTO_KIND.SHELF,
      blob,
      status: PHOTO_QUEUED,
      attempts: 0,
      lastError: null,
      createdAt: new Date().toISOString(),
    };
    hold(row);
    try {
      await storage().putPhoto(row);
    } catch (error) {
      unsaved.set(uuid, row);
      syncUnsavedHold();
      console.error('[collector/photoQueue] a photo was not written to the phone — kept in memory', error);
    }
    // No flush here: the report's own `facts` tap is about to be queued, and
    // taps go first — the queue draining (watched below) starts the upload.
    return { uuid, id };
  }

  // ── The invoice's paper ────────────────────────────────────────────────────

  /**
   * Keep the paper's photo until this shop's invoice is filed (one per shop; a
   * new one replaces it). When a filing of that shop already waits on the row
   * (status `queued`, collector/invoiceQueue.js) the Blob changes and what he
   * filed stays: the new paper rides it. Read and written in one transaction —
   * a rewrite of the whole row would drop the filing. The filing is also named
   * one millisecond later: a copy of it may be on the wire with the OLD paper,
   * and its answer must not remove the row before the new paper has gone.
   */
  async function holdInvoicePhoto({ planId, supplierId, blob }) {
    await ensureLoaded();
    const paper = {
      uuid: invoiceRowKey(planId, supplierId),
      planId: String(planId),
      lineId: null,
      supplierId: String(supplierId),
      userId: loadedFor ?? null,
      kind: PHOTO_KIND.INVOICE,
      blob,
      status: PHOTO_HELD,
      attempts: 0,
      lastError: null,
      createdAt: new Date().toISOString(),
    };
    try {
      await storage().patchPhoto(paper.uuid, (row) =>
        row?.kind === PHOTO_KIND.INVOICE && row.status === PHOTO_QUEUED
          ? { blob, filedAt: filedJustAfter(row.filedAt) }
          : paper,
      );
    } catch (error) {
      console.warn('[collector/photoQueue] the invoice photo was not kept on the phone', error);
    }
  }

  /** The paper on this shop's row — kept for a filing, or riding one that waits — or null. */
  async function heldInvoicePhoto({ planId, supplierId }) {
    await ensureLoaded();
    try {
      const all = await storage().loadPhotos(loadedFor ?? null);
      return all.find((row) => row.uuid === invoiceRowKey(planId, supplierId))?.blob ?? null;
    } catch {
      return null;
    }
  }

  /**
   * The shop's invoice reached the server: the kept paper goes. A row that
   * carries a filing still waiting is left alone — only the queue that sends it
   * may remove it.
   */
  async function releaseInvoicePhoto({ planId, supplierId }) {
    try {
      await storage().patchPhoto(invoiceRowKey(planId, supplierId), (row) =>
        row?.kind === PHOTO_KIND.INVOICE && row.status === PHOTO_QUEUED ? undefined : null,
      );
    } catch (error) {
      console.warn('[collector/photoQueue] the kept invoice photo was not removed', error);
    }
  }

  // ── Uploading ──────────────────────────────────────────────────────────────

  let failures = 0;
  let backoffTimer = null;
  let lockTimer = null;
  let tickTimer = null;
  let holdingDetector = false;
  let again = false;

  function clearTimers() {
    clearTimeout(backoffTimer);
    clearTimeout(lockTimer);
    backoffTimer = null;
    lockTimer = null;
  }

  function scheduleBackoff() {
    failures += 1;
    const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length) - 1];
    nextAttemptAt.value = Date.now() + delay;
    clearTimeout(backoffTimer);
    backoffTimer = setTimeout(() => {
      backoffTimer = null;
      flush({ reason: 'retry' });
    }, delay);
  }

  function resetBackoff() {
    failures = 0;
    nextAttemptAt.value = 0;
    clearTimeout(backoffTimer);
    backoffTimer = null;
  }

  async function withLock(work) {
    const locks = typeof navigator === 'undefined' ? null : navigator.locks;
    if (typeof locks?.request !== 'function') {
      await work();
      return true;
    }
    let ran = false;
    await locks.request(PHOTO_LOCK, { ifAvailable: true }, async (lock) => {
      if (!lock) return;
      ran = true;
      await work();
    });
    return ran;
  }

  /**
   * The line a photo was taken for, as the day has it at answer time: the day
   * on screen, else that plan's stored day. Null when the day no longer has it
   * (the card still reads, it just cannot name the shop).
   */
  async function lineOf(row) {
    const onScreen = useCollectorDayStore().day;
    let hit = sameId(onScreen?.id, row.planId) ? findLine(onScreen, row.lineId) : null;
    if (!hit) {
      try {
        const stored = await storage().load(loadedFor ?? null);
        hit = findLine(stored.days.find((record) => sameId(record.planId, row.planId))?.doc, row.lineId);
      } catch {
        /* no names: the card still says a photo did not arrive */
      }
    }
    return hit;
  }

  /**
   * ONE card per line for the photos the server refused (same collection and
   * same dismiss as the conflict cards). Another refused photo of the same line
   * grows its `count`; the uuids it was told for are kept, so a photo judged
   * twice (the phone restarted between the card and the delete) counts once.
   * Resolves true when the card is on the phone.
   */
  async function announceRefusal(row, status) {
    const actions = useCollectorActionsStore();
    await actions.ensureHydrated(); // a card from before a restart is on the screen: its count grows
    const hit = await lineOf(row);
    const id = `photo-refused:${row.planId}:${row.lineId}`;
    const before = actions.conflicts.find((card) => card.id === id);
    const uuids = [...(before?.uuids ?? [])];
    if (!uuids.includes(row.uuid)) uuids.push(row.uuid);
    const syncedAt = new Date().toISOString();
    return actions.addCard({
      id,
      planId: String(row.planId),
      state: PHOTO_CARD.REFUSED,
      reason: REFUSED_STATUS[status],
      type: null,
      planItemId: null,
      productName: hit?.line?.productName ?? before?.productName ?? null,
      memberCode: hit?.line?.memberCode ?? before?.memberCode ?? null,
      shopName: hit?.shop?.name ?? before?.shopName ?? null,
      shopKey: hit ? shopKey(hit.shop) : (before?.shopKey ?? null),
      count: uuids.length,
      uuids,
      syncedAt,
      seenAt: syncedAt,
    });
  }

  /**
   * One photo to the server. 'sent' | 'dropped' | 'retry' | 'paused'.
   * Online is fed from the outcome like the action layer's: any HTTP answer =
   * our server answers; no response = it does not.
   */
  async function sendRow(row) {
    const network = collectorNetwork();
    try {
      const stored = await sendCollectorPhoto({ blob: row.blob, planItemId: row.lineId, uuid: row.uuid, attempts: 1 });
      network.report(true);
      if (!stored?.id) return 'retry';
      try {
        await storage().putPhoto(receiptOf(row, stored));
      } catch (error) {
        // Not fatal: the Blob would be sent again after a restart, and the
        // server answers a repeat with the same id.
        console.warn('[collector/photoQueue] the upload receipt was not written to the phone', error);
      }
      forget(row.uuid);
      return 'sent';
    } catch (error) {
      const response = error?.response;
      const status = response?.status;
      if (!status) {
        network.report(false);
        return 'retry';
      }
      network.report(true);
      if (status === 401 || status === 419) return 'paused';
      if (status >= 500 || status === 408 || status === 429) return 'retry';
      // Only our API's own JSON may judge a photo. A proxy's 413 or a captive
      // portal's 403 is the network talking: keep the Blob and try again.
      if (!(status in REFUSED_STATUS) || !isOurApi(response)) {
        console.warn(`[collector/photoQueue] a ${status} that is not our API's verdict for a photo — kept, tried again`, row.uuid);
        return 'retry';
      }
      // 403 / 404 / 422 from our API: the server will never take this one. Told
      // once; then it leaves so the photos behind it are not held up. The Blob
      // stays on the phone only if the card could not be written (it would be
      // judged and told again after a restart rather than vanish).
      console.warn(`[collector/photoQueue] the server refused a photo (${status}) — dropped`, row.uuid);
      const told = await announceRefusal(row, status);
      forget(row.uuid);
      if (told) {
        storage()
          .deletePhoto(row.uuid)
          .catch((deleteError) => console.warn('[collector/photoQueue] a refused photo was not removed from the phone', deleteError));
      }
      return 'dropped';
    }
  }

  /** Upload what waits, one at a time. Resolves false when a photo was kept for a retry. */
  async function uploadRounds() {
    await writeUnsaved();
    await reconcile();
    const actions = useCollectorActionsStore();
    const release = usePwa().holdReload('collector-photos');
    let uploaded = 0;
    let clean = true;
    try {
      for (;;) {
        // A tap made meanwhile goes first: the watcher flushes again when it is answered.
        if (actions.pendingCount > 0 || authPaused.value) break;
        const row = rows.value.find(isQueued);
        if (!row) break;
        const outcome = await sendRow(row);
        if (outcome === 'sent') {
          uploaded += 1;
          resetBackoff();
        } else if (outcome === 'paused') {
          pauseForAuth();
          scheduleBackoff();
          clean = false;
          break;
        } else if (outcome === 'retry') {
          scheduleBackoff();
          clean = false;
          break;
        }
      }
    } finally {
      release();
    }
    // The server now has them: let the day swap the Blob for the real picture.
    if (uploaded > 0) useCollectorDayStore().refresh();
    return clean;
  }

  /**
   * Upload what waits — if the action queue is empty and the login is alive.
   * `reason` is only for the timed paths: the 60 s tick respects the backoff.
   */
  async function flush({ reason = 'add' } = {}) {
    await ensureLoaded();
    if (!rows.value.some(isQueued) && unsaved.size === 0) return;
    const actions = useCollectorActionsStore();
    if (authPaused.value || actions.pendingCount > 0 || actions.flushing) return;
    if (flushing.value) {
      again = true;
      return;
    }
    if ((reason === 'tick' || reason === 'resume') && Date.now() < nextAttemptAt.value) return;

    flushing.value = true;
    let clean = true;
    try {
      const ran = await withLock(async () => {
        do {
          again = false;
          clean = await uploadRounds();
        } while (again && clean);
      });
      if (!ran && !lockTimer) {
        lockTimer = setTimeout(() => {
          lockTimer = null;
          flush({ reason: 'lock' });
        }, LOCK_RETRY_MS);
      }
    } catch (error) {
      clean = false;
      console.error('[collector/photoQueue] upload failed', error);
    } finally {
      flushing.value = false;
    }
    if (again && clean) {
      again = false;
      flush({ reason: 'again' });
    }
  }

  // ── Triggers ─────────────────────────────────────────────────────────────────

  function onVisibility() {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') flush({ reason: 'visible' });
  }

  function onPageShow() {
    flush({ reason: 'pageshow' });
  }

  function keepDetector(waiting) {
    if (waiting && !holdingDetector) {
      collectorNetwork().start();
      holdingDetector = true;
    } else if (!waiting && holdingDetector) {
      collectorNetwork().stop();
      holdingDetector = false;
    }
    if (waiting && !tickTimer) {
      tickTimer = setInterval(() => flush({ reason: 'tick' }), TICK_MS);
    } else if (!waiting && tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  }

  /**
   * Arm the page triggers (idempotent) — called from the action layer's
   * `start()`, so App.vue's boot and every collector page arm both queues
   * together. The engine's own state is watched below, from the moment the
   * store exists.
   */
  function start() {
    if (started.value) return;
    started.value = true;
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') window.addEventListener('pageshow', onPageShow);
    ensureLoaded()
      .then(() => flush({ reason: 'start' }))
      .catch((error) => console.error('[collector/photoQueue] the first upload round failed', error));
  }

  const actionsStore = useCollectorActionsStore();

  // Every tap sent AND the flush that sent them finished → the photos go.
  // (`flushing` matters: the count reaches 0 while that flush is still running.)
  watch(
    () => [actionsStore.pendingCount, actionsStore.flushing],
    ([waiting, busy]) => {
      if (waiting === 0 && !busy) flush({ reason: 'drained' });
    },
  );

  // The login is back → send what waited.
  watch(authPaused, (paused, wasPaused) => {
    if (!paused && wasPaused) flush({ reason: 'resume' });
  });

  // Back online → send what waited.
  watch(collectorNetwork().isOnline, (online) => {
    if (online) flush({ reason: 'online' });
  });

  // While photos wait: keep the shared detector running and tick every 60 s.
  watch([() => rows.value.length, started], ([waiting, armed]) => keepDetector(armed && waiting > 0), { immediate: true });

  /** Forget everything in memory (tests; another account on this phone). */
  function clear() {
    resetMemory();
    resetBackoff();
    loading = null;
    loadedFor = undefined;
    keepDetector(false);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') window.removeEventListener('pageshow', onPageShow);
    started.value = false;
  }

  // The hold asks what memory holds (a write may not be on disk yet).
  registerLocalWork((userId) => loadedFor === normalizeUserId(userId) && rows.value.length > 0, 'photos');

  return {
    rows,
    pendingCount,
    localPhotos,
    flushing,
    started,
    nextAttemptAt,
    ensureLoaded,
    add,
    holdInvoicePhoto,
    heldInvoicePhoto,
    releaseInvoicePhoto,
    flush,
    start,
    clear,
  };
});
